import { useCallback, useRef, useState } from "react";

const V3_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const V2_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY;

const CHALLENGE_FAILED = "Could not verify. Please try the challenge again.";
const UNAVAILABLE = "Unable to verify reCAPTCHA. Please try again.";

let scriptPromise = null;

const loadRecaptcha = () => {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha) return resolve(window.grecaptcha);

    const script = document.createElement("script");
    script.id = "recaptcha-script";
    // recaptcha.net is Google's alternative host for networks that block
    // google.com (corporate proxies/VPNs).
    script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${V3_SITE_KEY}`;
    script.async = true;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load reCAPTCHA"));
    };

    document.body.appendChild(script);
  });

  return scriptPromise;
};

const grecaptchaReady = async () => {
  const grecaptcha = await loadRecaptcha();
  await new Promise((resolve) => grecaptcha.ready(resolve));
  return grecaptcha;
};

const verifyToken = async (token, action, version) => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}verify-recaptcha`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, action, version }),
  });

  return res.json();
};

export const useRecaptcha = (action) => {
  const [challengeVisible, setChallengeVisible] = useState(false);
  const [challengeError, setChallengeError] = useState(null);
  const challengeRef = useRef(null);
  const widgetIdRef = useRef(null);
  const pendingRef = useRef(null);

  const awaitChallenge = useCallback(async () => {
    const grecaptcha = await grecaptchaReady();

    if (widgetIdRef.current === null) {
      widgetIdRef.current = grecaptcha.render(challengeRef.current, {
        sitekey: V2_SITE_KEY,
        callback: (token) => pendingRef.current?.resolve(token),
        "expired-callback": () => grecaptcha.reset(widgetIdRef.current),
        "error-callback": () =>
          pendingRef.current?.reject(new Error("challenge unavailable")),
      });
    } else {
      grecaptcha.reset(widgetIdRef.current);
    }

    return new Promise((resolve, reject) => {
      pendingRef.current = { resolve, reject };
    });
  }, []);

  const verifyHuman = useCallback(async () => {
    setChallengeError(null);

    try {
      const grecaptcha = await grecaptchaReady();
      const token = await grecaptcha.execute(V3_SITE_KEY, { action });

      if (!(await verifyToken(token, action, "v3")).blocked) {
        return { ok: true, token };
      }

      setChallengeVisible(true);

      let verified = null;

      while (!verified) {
        const challengeToken = await awaitChallenge();

        if ((await verifyToken(challengeToken, action, "v2")).blocked) {
          setChallengeError(CHALLENGE_FAILED);
        } else {
          verified = challengeToken;
        }
      }

      setChallengeVisible(false);
      return { ok: true, token: verified };
    } catch (err) {
      console.error("reCAPTCHA error:", err);
      return { ok: false, error: UNAVAILABLE };
    }
  }, [action, awaitChallenge]);

  return { verifyHuman, challengeVisible, challengeError, challengeRef };
};
